import { type JSX, useState } from "react";

import { componentManager, rwc } from "@frontend/bridge/ComponentManager";
import {
  Outlet,
  RouterProvider,
  createRootRoute,
  createRouter,
} from "@tanstack/react-router";

import {
  type ProjectRouterDataProps,
  projectRouterDataSchema,
} from "./ProjectRouter.schema";
import { projectRoutes } from "./routes";

declare module "@frontend/bridge/types" {
  interface ComponentRegistryMap {
    "project-router": { props: ProjectRouterDataProps; handle: undefined };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rwc-project-router": HTMLElement;
  }
}

/**
 * The projects as pages rather than as one island: the list, and a project
 * opened over it, each with an address of its own.
 *
 * The router is this shell's, not the feature's. `projectRoutes` says what
 * lives under the path without knowing which one it is, so the same subtree
 * serves `/project/subscription` here and any other prefix elsewhere.
 *
 * Symfony must serve every path under `basepath`, which is what the catch-all
 * on `eco_pro_project_subscription_index` does.
 */
export function RWCProjectRouter({
  basepath,
  type,
}: ProjectRouterDataProps): JSX.Element {
  // Built once: a router remade on every render would lose where it is.
  const [router] = useState(() => {
    const rootRoute = createRootRoute({ component: Outlet });

    return createRouter({
      routeTree: rootRoute.addChildren([projectRoutes(rootRoute, { type })]),
      basepath,
      // Closing the editor goes back to the list, and its address is the page
      // itself rather than the page with a slash hanging off it.
      trailingSlash: "never",
    });
  });

  return <RouterProvider router={router} />;
}

componentManager.register(
  rwc("project-router", {
    component: RWCProjectRouter,
    dataSchema: projectRouterDataSchema,
  }),
);
